import React, { useEffect, useState } from "react";
import RenderItemForUserForm from "../components/FormBuilder/subcomponents/form-preview/RederItemForUserForm";
import { useParams } from "react-router-dom";
import axios from "axios";
import useModalStrip from "../global-hooks/useModalStrip";
import {
  openCircularProgress,
  closeCircularProgress,
} from "../redux/uireducers/progress";
import { useAppDispatch } from "../redux/hooks";
import CameraWithCrop from "../components/FormBuilder/subcomponents/CameraWithCrop";
import Loader from "../utils/Loader";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import moment from "moment";
import { FormControlNames } from "../utils/formBuilderUtils";
import "./UserFormBuilderPage.css";

const previewWindowStyle = {
  backgroundColor: "white",
  height: "88vh",
  overflowY: "scroll",
  border: "1px solid rgba(0,0,0,0.1)",
  borderRadius: "9px",
  boxShadow: "0 9px 90px #efefef",
  marginLeft: "auto",
  marginRight: "auto",
  width: "60%",
};

const UserFormBuilderPage = () => {
  const [componentIndex, setComponentIndex] = useState(0);
  const [formLayoutComponents, setFormLayoutComponents] = useState();
  const [component, setComponent] = useState();
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formName, setFormName] = useState("");
  const [formValues, setFormValues] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const [showCamera, setShowCamera] = useState(false); // State to handle camera visibility
  const [isLoading, setIsLoading] = useState(false); // State for form submission loading
  const [validationErrors, setValidationErrors] = useState({}); // State to track validation errors
  const { showModalStrip } = useModalStrip();
  const dispatch = useAppDispatch();
  const { formId } = useParams();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [isConvertingFile, setIsConvertingFile] = useState(false);

  //----pdf download functions start---------------------

  const downloadPDF = async () => {
    const previewElement = document.querySelector(".preview-modal"); // Select the preview modal
    if (!previewElement) {
      alert("Preview content not found!");
      return;
    }

    // Use html2canvas to capture the content
    const canvas = await html2canvas(previewElement, {
      scale: 2, // Higher resolution for better quality
      useCORS: true, // Handle cross-origin images
      backgroundColor: null, // Transparent background
    });

    const imgData = canvas.toDataURL("image/png"); // Get image data

    // Dynamically calculate dimensions
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const pageOrientation = imgWidth > imgHeight ? "l" : "p"; // Set orientation based on content
    const pdf = new jsPDF(pageOrientation, "mm", [imgWidth * 0.264583, imgHeight * 0.264583]); // Dynamically set page size

    // Fit the image fully into the page
    pdf.addImage(imgData, "PNG", 0, 0, pdf.internal.pageSize.getWidth(), pdf.internal.pageSize.getHeight());

    pdf.save("PreviewContent.pdf");
  };

  //----pdf download functions end---------------------

  // Form fields update handler
  const handleChange = (id, value, type) => {
    let processedValue = value;

    if (type === "file" && (value instanceof File || value instanceof Blob)) {
      processedValue = value;
    } else if (type === "date") {
      processedValue = moment(value).format("DD-MM-YYYY");
    }

    setFormValues((prevValues) => ({
      ...prevValues,
      [id]: processedValue,
    }));
  };

  const nextPrevIndex = (val) => {
    setComponentIndex((prev) => prev + val);
  };

  const getFormByFormId = async (id) => {
    dispatch(openCircularProgress());
    try {
      const response = await axios.get(`${API_BASE_URL}/templates/${id}`);
      if (response) {
        setFormName(response?.data?.formName);
        document.title = response?.data?.formName ?? "Kashish Enterprises"
        setFormLayoutComponents(response.data);
        dispatch(closeCircularProgress());
      }
    } catch (error) {
      showModalStrip("error", "Form not found!", 5000);
      dispatch(closeCircularProgress());
    }
  };

  // New validation function for required fields
  const validateForm = () => {
    const requiredFields = formLayoutComponents?.formLayoutComponents?.flatMap((component) =>
      component.children?.filter(child => child.required)
    ) || [];

    let errors = {};
    let isValid = true;

    for (let field of requiredFields) {
      if (!formValues[field.id]) {
        isValid = false;
        errors[field.id] = `${field.labelName} is required!`; // Store error message for the field
      }
    }

    setValidationErrors(errors); // Update the state with the errors
    return isValid; // Return validation result
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (isConvertingFile) {
      return Swal.fire({
        text: "Please wait, image conversion is still in progress.",
        icon: "info",
        confirmButtonText: "Okay",
      });
    }
    try {
      console.log("formValues", formValues, component?.children);
      e.preventDefault();

      //check validation
      //get the required value
      const requiredValues = component?.children.filter((ele) => ele?.required);
      for (let i = 0; i < requiredValues.length; i++) {
        const element = requiredValues[i];
        if (!formValues.hasOwnProperty(element.id)) {
          return Swal.fire({
            text: `${element?.labelName} is required`,
            icon: "warning",
            confirmButtonText: "Okay",
          });
          break;
        }

        if (element?.controlName === "mobile-field" && formValues[element.id].length != 10) {
          return Swal.fire({
            text: `${element?.labelName} must be 10 digit`,
            icon: "warning",
            confirmButtonText: "Okay",
          });
        }
      }
      setIsLoading(true);  // Set loading state to true
      dispatch(openCircularProgress());

      const allFields =
        formLayoutComponents?.formLayoutComponents?.flatMap((component) => component.children) || [];

      const fileFieldTypes = [
        FormControlNames.IMAGEUPLOAD,
        FormControlNames.CAPTURECLICK,
        FormControlNames.FILEUPLOAD,
        FormControlNames.SIGNATURE,
        FormControlNames.SCANCODE,
      ];

      const result = {};
      const formData = new FormData();

      for (const item of allFields) {
        const value = formValues[item.id];
        if (!value) continue;

        const isFileField = fileFieldTypes.includes(item.controlName);

        if (isFileField) {
          if (value instanceof File) {
            formData.append("imgfiles", value, value.name);
          } else if (value instanceof Blob) {
            formData.append("imgfiles", value, `${item.id}-capture.png`);
          } else if (typeof value === "string" && value.startsWith("blob:")) {
            const blob = await fetch(value).then((res) => res.blob());
            formData.append("imgfiles", blob, `${item.id}-capture.jpg`);
          } else if (typeof value === "string" && value.startsWith("data:image/")) {
            const res = await fetch(value);
            const blob = await res.blob();
            formData.append("imgfiles", blob, `${item.id}-capture.png`);
          }
          continue;
        }
        result[item.labelName] = value;
      }

      const payload = {
        formId,
        resData: result,
      };

      formData.append("mainResponse", JSON.stringify(payload));

      const token = localStorage.getItem("userToken");
      const headers = { Authorization: `Bearer ${token}` };

      const submitRes = await axios.post(`${API_BASE_URL}/user-responses`, formData, {
        headers,
      });


      if (submitRes?.data?.status === "SUCCESS") {
        dispatch(closeCircularProgress());
        setIsLoading(false);  // Stop loading
        setFormSubmitted(true);
        setShowPreview(false);
        Swal.fire({ text: submitRes?.data?.message, icon: "success" });
      } else {
        setIsLoading(false);
        setShowPreview(false);
        Swal.fire({ text: submitRes?.data?.message, icon: "warning" });
      }
    } catch (error) {
      dispatch(closeCircularProgress());
      setIsLoading(false);  // Stop loading on error
    }
  };

  const handlePreview = () => {
    setShowPreview(true);
  };

  const closePreview = () => {
    setShowPreview(false);
  };

  useEffect(() => {
    setComponent(formLayoutComponents?.formLayoutComponents[componentIndex]);
  }, [formLayoutComponents, componentIndex]);

  useEffect(() => {
    getFormByFormId(formId);
  }, [formId]);

  useEffect(() => {
    return () => {
      Object.values(formValues).forEach((value) => {
        if (typeof value === "string" && value.startsWith("blob:")) {
          URL.revokeObjectURL(value);
        }
      });
    };
  }, [formValues]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const popupElement = document.querySelector(".preview-modal .id-card-container");
      if (showPreview && popupElement && !popupElement.contains(event.target)) {
        closePreview();  // Close the popup
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [showPreview]);

  useEffect(() => {
    document.title = formName; // Update the tab name dynamically
  }, [formName]); // Runs whenever formName changes

  return (
    <div className="user-form-container">
      <div className="preview-window">
        {/* Show loader if form is submitting */}
        {isLoading && <Loader />}
        {console.log("formLayoutComponents : ", formLayoutComponents)}
        <div>
          {!formSubmitted ? (
            // Check if the form is active
            formLayoutComponents?.isActive === false ? (
              <div className="text-center">
                <h3 className="text-info">
                  This link has expired. Please contact the administrator for further assistance or more details. We are here to help!
                </h3>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="step-indicator">
                  <div className="step-text">
                    <span className="step-number">Step {componentIndex + 1}</span>
                    <span className="step-total"> of {formLayoutComponents?.formLayoutComponents?.length}</span>
                  </div>
                </div>
                <div className="form-header">
                  {formLayoutComponents?.logo &&
                    <div className="logo-section">
                      <img
                        src={`${process.env.REACT_APP_IMG_URL}/${formLayoutComponents?.logo}`}
                        alt="Dynamic Form"
                        className="logo-image"
                      />
                    </div>
                  }

                  <div className={`title-section ${formLayoutComponents?.logo ? 'left' : 'center'}`}>
                    <h2 className="form-title">{formLayoutComponents?.formName}</h2>
                  </div>
                </div>

                {/* Section heading */}
                <div className="section-heading">
                  <h4 className="heading-title">{component?.container?.heading}</h4>
                  <p className="heading-subtitle">{component?.container?.subHeading}</p>
                </div>

                {component?.children?.map((child) => (
                  <div key={child.id} className="form-field">
                    <label className="field-label">
                      {child.labelName}
                      {child.required && <span className="required-asterisk">*</span>}
                    </label>
                    <RenderItemForUserForm
                      key={child.id}
                      item={child}
                      value={formValues[child?.id]}
                      onChange={handleChange}
                      isConvertingFile={isConvertingFile}
                      setIsConvertingFile={setIsConvertingFile}
                    />
                  </div>
                ))}

                {/* Navigation Buttons */}
                <div className="button-container">
                  <button
                    type="button"
                    className="btn btn-secondary btn-back"
                    disabled={componentIndex === 0}
                    onClick={() => nextPrevIndex(-1)}
                  >
                    ← Back
                  </button>

                  {componentIndex < formLayoutComponents?.formLayoutComponents?.length - 1 ? (
                    <button
                      type="button"
                      className="btn btn-next"
                      onClick={() => nextPrevIndex(1)}
                    >
                      Next →
                    </button>
                  ) : (
                    <div className="d-flex gap-2" style={{ gap: '8px', display: 'flex' }}>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handlePreview}
                      >
                        Preview
                      </button>
                      <button
                        type="submit"
                        className="btn btn-submit"
                      >
                        Submit
                      </button>
                    </div>
                  )}
                </div>
              </form>
            )
          ) : (
            <div className="text-center style={{padding:'40px'}}">
              <h3 className="text-success">
                Thank you! Your response to {formName} is submitted!
              </h3>
            </div>
          )}
        </div>

        {/* Preview Modal */}
        {showPreview && (
          <div className="preview-modal" onClick={(e) => e.target.classList.contains('preview-modal') && closePreview()}>
            <div className="id-card-container">
              <div className="id-card-header">
                <h3>{formName}</h3>
              </div>
              {formLayoutComponents?.formLayoutComponents?.map((comp) =>
                comp?.children?.map((child) => {
                  let value = formValues[child.id];
                  if (value && value._isAMomentObject) value = value.format("DD-MM-YYYY");
                  if (value instanceof Date) {
                    value = `${String(value.getDate()).padStart(2, '0')}-${String(value.getMonth() + 1).padStart(2, '0')}-${value.getFullYear()}`;
                  }
                  const isImage = ["Image", "Camera", "Upload", "Signature"].includes(child.displayText);
                  const fileVal = formValues[child.id];
                  return (
                    <div key={child.id} className="id-card-body">
                      {isImage && fileVal && (
                        <div className="id-card-photo">
                          <img
                            src={
                              typeof fileVal === "string" && fileVal.startsWith("data:image/")
                                ? fileVal
                                : fileVal instanceof Blob
                                  ? URL.createObjectURL(fileVal)
                                  : fileVal
                            }
                            alt="Captured"
                            className="photo"
                            style={{ width: "100px", height: "100px", objectFit: "cover" }}
                          />
                        </div>
                      )}
                      <div className="id-card-details">
                        <div className="id-card-field">
                          {!isImage && <strong>{child.labelName}</strong>}
                          <span>: {isImage ? '' : (value || "N/A")}</span>
                        </div>
                      </div>
                    </div>
                  )
                })
              )}
              <div className="d-flex justify-content-between mt-3" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px', gap: '8px' }}>
                <button type="button" className="btn btn-light" onClick={closePreview}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handleSubmit}>
                  Submit Form
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserFormBuilderPage;
